"use client";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "next-view-transitions";
import React from "react";

type Tone = "green" | "blue" | "amber" | "neutral";

// Demo loop: one call at a time. When it ends, it moves into the Afterburn
// feed with its outcome and actions, and the next lead in the queue is dialed.
// Six leads so nobody shows up in the queue and the feed at the same time.
type DemoLead = {
  n: 1 | 2 | 3 | 4 | 5 | 6;
  name: string;
  scenario: "s1" | "s2" | "s3" | "s4";
  actionKeys: string[];
  tone: Tone;
  startSeconds: number;
};

const LEADS: DemoLead[] = [
  { n: 1, name: "Marcus Weber", scenario: "s1", actionKeys: ["a1", "a2"], tone: "green", startSeconds: 154 },
  { n: 2, name: "Anna Schmidt", scenario: "s2", actionKeys: ["a1", "a2"], tone: "blue", startSeconds: 212 },
  { n: 3, name: "Tom Becker", scenario: "s3", actionKeys: ["a1"], tone: "amber", startSeconds: 71 },
  { n: 4, name: "Lisa Roth", scenario: "s4", actionKeys: ["a1", "a2"], tone: "neutral", startSeconds: 38 },
  { n: 5, name: "Ahmed Faris", scenario: "s2", actionKeys: ["a1", "a2"], tone: "blue", startSeconds: 187 },
  { n: 6, name: "Julia Wagner", scenario: "s1", actionKeys: ["a1", "a2"], tone: "green", startSeconds: 96 },
];

const CALL_MS = 4500;
const FEED_SIZE = 3;

const TONE_DOT: Record<Tone, string> = {
  green: "bg-emerald-500",
  blue: "bg-sky-500",
  amber: "bg-amber-500",
  neutral: "bg-neutral-500",
};

const leadAt = (step: number) =>
  LEADS[((step % LEADS.length) + LEADS.length) % LEADS.length];

export const AfterburnSection = () => {
  return (
    <section
      id="afterburn"
      className="dark relative w-full overflow-hidden bg-neutral-950 scroll-mt-24"
    >
      <SectionBackground />
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28">
        <BrandReveal />
        <div className="mt-10 lg:mt-14">
          <DialerFlowScene />
        </div>
        <div className="mt-16 lg:mt-20">
          <FeatureSplit />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => {
  return (
    <>
      <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
    </>
  );
};

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("afterburn");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <FlameIcon className="h-3.5 w-3.5" />
        Afterburn
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Demo loop (Dialer → Afterburn)
// ============================================================================

const DialerFlowScene = () => {
  const reduceMotion = useReducedMotion();
  // `step` is the call in progress; the FEED_SIZE calls before it are done.
  const [step, setStep] = React.useState(FEED_SIZE);

  React.useEffect(() => {
    if (reduceMotion) return;
    const id = setInterval(() => setStep((s) => s + 1), CALL_MS);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const done = Array.from({ length: FEED_SIZE }, (_, i) => step - 1 - i);

  return (
    <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-950">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <DialerPane step={step} />
        <FeedPane steps={done} />
      </div>
    </div>
  );
};

const PaneHeader = ({
  icon,
  label,
  meta,
}: {
  icon: React.ReactNode;
  label: string;
  meta: string;
}) => (
  <div className="flex items-center justify-between gap-3">
    <span className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-200">
      {icon}
      {label}
    </span>
    <span className="text-[11px] text-neutral-500">{meta}</span>
  </div>
);

const DialerPane = ({ step }: { step: number }) => {
  const t = useTranslations("afterburn");
  const locale = useLocale();
  const lead = leadAt(step);
  const [open, close] = locale === "de" ? ["„", "“"] : ["“", "”"];

  return (
    <div className="md:col-span-5 border-b md:border-b-0 md:border-r border-neutral-800 p-5">
      <PaneHeader
        icon={<span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}
        label={t("live_label")}
        meta={t("queue_label")}
      />

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25 }}
          className="mt-4 rounded-xl border border-neutral-800 bg-neutral-900 p-4"
        >
          <div className="flex items-center justify-between text-[11px]">
            <span className="font-medium text-emerald-400">{t("in_call")}</span>
            <CallTimer start={lead.startSeconds} />
          </div>
          <div className="mt-2 text-sm font-semibold text-white">{lead.name}</div>
          <div className="text-xs text-neutral-500">{t(`lead_${lead.n}_company`)}</div>
          <p className="mt-3 flex items-start gap-2 text-[13px] leading-snug text-neutral-300">
            <WaveformIcon />
            <span>
              {open}
              {t(`${lead.scenario}_transcript`)}
              {close}
            </span>
          </p>
        </motion.div>
      </AnimatePresence>

      <div className="mt-5 text-[11px] font-medium text-neutral-500">
        {t("next_in_queue")}
      </div>
      <ul className="mt-2 space-y-1.5">
        {[1, 2].map((offset) => {
          const next = leadAt(step + offset);
          return (
            <li key={step + offset} className="flex items-center justify-between gap-3 text-xs">
              <span className="text-neutral-300">{next.name}</span>
              <span className="truncate text-neutral-500">{t(`lead_${next.n}_company`)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const FeedPane = ({ steps }: { steps: number[] }) => {
  const t = useTranslations("afterburn");
  return (
    <div className="md:col-span-7 p-5">
      <PaneHeader
        icon={<FlameIcon className="h-3.5 w-3.5 text-red-500" />}
        label={t("autodoc_label")}
        meta={t("autodoc_sublabel")}
      />
      <ul className="mt-4 space-y-2">
        <AnimatePresence mode="popLayout" initial={false}>
          {steps.map((s, i) => {
            const lead = leadAt(s);
            const latest = i === 0;
            const actions = [
              t("note_written"),
              ...lead.actionKeys.map((a) => t(`${lead.scenario}_${a}`)),
            ];
            return (
              <motion.li
                key={s}
                layout
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: latest ? 1 : 0.55, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className={cn(
                  "rounded-xl border px-4 py-3",
                  latest ? "border-neutral-700 bg-neutral-900" : "border-neutral-800"
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate text-sm font-medium text-neutral-100">
                    {lead.name}
                  </span>
                  <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-neutral-300">
                    <span className={cn("h-1.5 w-1.5 rounded-full", TONE_DOT[lead.tone])} />
                    {t(`${lead.scenario}_outcome`)}
                  </span>
                </div>
                <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                  {actions.map((action, j) => (
                    <motion.li
                      key={action}
                      initial={latest ? { opacity: 0 } : false}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 + j * 0.3 }}
                      className="inline-flex items-center gap-1.5 text-xs text-neutral-400"
                    >
                      <CheckIcon className="h-3 w-3 text-red-500" />
                      {action}
                    </motion.li>
                  ))}
                </ul>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>
    </div>
  );
};

const CallTimer = ({ start }: { start: number }) => {
  const [seconds, setSeconds] = React.useState(start);
  React.useEffect(() => {
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = Math.floor(seconds / 60).toString().padStart(2, "0");
  const ss = (seconds % 60).toString().padStart(2, "0");
  return (
    <span className="font-mono tabular-nums text-neutral-500">
      {mm}:{ss}
    </span>
  );
};

const WaveformIcon = () => (
  <svg
    width="14"
    height="10"
    viewBox="0 0 14 10"
    fill="none"
    className="text-emerald-500 flex-shrink-0"
    aria-hidden
  >
    <rect x="0" y="3" width="1.5" height="4" fill="currentColor" rx="0.5">
      <animate attributeName="height" values="4;7;4" dur="0.9s" repeatCount="indefinite" />
      <animate attributeName="y" values="3;1.5;3" dur="0.9s" repeatCount="indefinite" />
    </rect>
    <rect x="3" y="2" width="1.5" height="6" fill="currentColor" rx="0.5">
      <animate attributeName="height" values="6;3;6" dur="0.7s" repeatCount="indefinite" />
      <animate attributeName="y" values="2;3.5;2" dur="0.7s" repeatCount="indefinite" />
    </rect>
    <rect x="6" y="1" width="1.5" height="8" fill="currentColor" rx="0.5">
      <animate attributeName="height" values="8;4;8" dur="1.1s" repeatCount="indefinite" />
      <animate attributeName="y" values="1;3;1" dur="1.1s" repeatCount="indefinite" />
    </rect>
    <rect x="9" y="2" width="1.5" height="6" fill="currentColor" rx="0.5">
      <animate attributeName="height" values="6;2;6" dur="0.8s" repeatCount="indefinite" />
      <animate attributeName="y" values="2;4;2" dur="0.8s" repeatCount="indefinite" />
    </rect>
    <rect x="12" y="3" width="1.5" height="4" fill="currentColor" rx="0.5">
      <animate attributeName="height" values="4;6;4" dur="1s" repeatCount="indefinite" />
      <animate attributeName="y" values="3;2;3" dur="1s" repeatCount="indefinite" />
    </rect>
  </svg>
);

// ============================================================================
// Block 3 — Feature Split (Call Documentator ⊕ Workflows)
// ============================================================================

const FeatureSplit = () => {
  const t = useTranslations("afterburn");
  const locale = useLocale();

  return (
    <div>
      <div className="text-center max-w-2xl mx-auto">
        <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white tracking-tight">
          {t("split_heading")}
        </h3>
        <p className="mt-4 text-sm md:text-base text-neutral-400 leading-relaxed">
          {t("split_lead")}
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        <FeatureCard
          icon={<IconNotes />}
          title={t("cd_title")}
          subtitle={t("cd_subtitle")}
          bullets={[t("cd_b1"), t("cd_b2"), t("cd_b3")]}
          ctaLabel={t("cd_cta")}
          href={`/${locale}/features/call-documentator`}
        />
        <FeatureCard
          icon={<IconBolt />}
          title={t("wf_title")}
          subtitle={t("wf_subtitle")}
          bullets={[t("wf_b1"), t("wf_b2"), t("wf_b3"), t("wf_b4")]}
          ctaLabel={t("wf_cta")}
          href={`/${locale}/features/workflows`}
        />
      </div>
    </div>
  );
};

const FeatureCard = ({
  icon,
  title,
  subtitle,
  bullets,
  ctaLabel,
  href,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  bullets: string[];
  ctaLabel: string;
  href: string;
}) => (
  <Link
    href={href}
    className="group relative rounded-2xl border border-neutral-800 bg-neutral-900/50 p-6 md:p-7 overflow-hidden transition-colors hover:border-neutral-600 flex flex-col"
  >
    <div className="relative flex-1">
      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h4 className="mt-4 text-lg md:text-xl font-bold text-white tracking-tight">
        {title}
      </h4>
      <p className="mt-1.5 text-sm text-neutral-400 leading-relaxed">
        {subtitle}
      </p>
      <ul className="mt-5 space-y-2.5">
        {bullets.map((b, i) => (
          <li
            key={i}
            className="flex items-start gap-2.5 text-sm text-neutral-300"
          >
            <span className="mt-2 flex h-1 w-1 flex-shrink-0 rounded-full bg-neutral-500" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
    <div className="relative mt-6 flex items-center gap-1.5 text-sm font-semibold text-neutral-300 group-hover:text-white transition-colors">
      <span>{ctaLabel}</span>
      <svg
        viewBox="0 0 16 16"
        className="h-4 w-4 transition-transform group-hover:translate-x-1"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M3 8h10M9 4l4 4-4 4" />
      </svg>
    </div>
  </Link>
);

// ============================================================================
// Icons
// ============================================================================

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5c1.3 3.1 3.8 4.6 4.7 6.9.9 2.3.3 4.7-1.4 6.4-.6.6-1.3 1-2.1 1.2.5-.9.6-2 .2-3-.5-1.2-1.7-1.9-2.9-1.9-.3 1-.9 1.9-1.7 2.5-1.7 1.3-2.3 3.5-1.6 5.4.5 1.4 1.6 2.6 3 3-3.7-.2-7-3.1-7.4-6.9-.4-4.1 2.4-7.7 5.5-10.1C10.3 4.7 11.4 3.6 12 2.5z" />
    </svg>
  );
}

function IconNotes() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M4 5a2 2 0 0 1 2-2h9l5 5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5Z" />
      <path d="M15 3v4a2 2 0 0 0 2 2h4" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  );
}

function IconBolt() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4 12l5 5L20 6" />
    </svg>
  );
}
